import { Box, useToast } from "@chakra-ui/react";
import { FormattedMessage, IntlShape, useIntl } from "react-intl";

import { RanaButton, RanaErrors, RanaModal, UndoIcon } from "@nens/ranalib";

import { FILE_REF_KEY } from "@/config/query-parameter-consts";
import { cancelButtonText } from "@/translations";

import { useRevertFileVersion } from "../api/revert-file-version";

interface ProjectConfirmRevertVersionModalProps {
  fileId: string;
  fileRef: string;
  onClose: () => void;
}

export const revertFileToVersionAction = ({
  action,
  intl,
}: {
  action: () => void;
  intl: IntlShape;
}) => ({
  icon: <UndoIcon />,
  label: intl.formatMessage({
    id: "button.revert_previous_version",
    defaultMessage: "Revert file to this version",
  }),
  action,
});

export const RevertFileToVersionButton = ({ action }: { action: () => void }) => {
  const intl = useIntl();

  const { icon, label } = revertFileToVersionAction({ action, intl });

  return (
    <RanaButton variant="secondary" leftIcon={icon} onClick={action}>
      {label}
    </RanaButton>
  );
};

export function ProjectConfirmRevertVersionModal({
  onClose,
  fileId,
  fileRef,
}: ProjectConfirmRevertVersionModalProps) {
  const intl = useIntl();
  const { mutate: revertFileVersion, errors } = useRevertFileVersion();
  const toast = useToast();

  return (
    <RanaModal
      isOpen
      maxW="lg"
      onClose={onClose}
      topBar={<FormattedMessage id="header.revert_files_confirm" defaultMessage="Are you sure?" />}
      buttons={[
        {
          children: cancelButtonText,
          variant: "tertiary",
          onClick: onClose,
        },
        {
          children: intl.formatMessage({
            id: "button.revert",
            defaultMessage: "Revert",
          }),
          variant: "primary",
          onClick: () => {
            revertFileVersion(
              { id: fileId, srcRef: fileRef },
              {
                onSuccess: () => {
                  toast({
                    title: intl.formatMessage({
                      id: "success",
                      defaultMessage: "Success!",
                    }),
                    description: intl.formatMessage({
                      id: "success.reverted_version",
                      defaultMessage: "Successfully reverted file version.",
                    }),
                  });

                  const searchParams = new URLSearchParams(window.location.search);
                  if (searchParams.has(FILE_REF_KEY)) {
                    searchParams.delete(FILE_REF_KEY);
                    const newUrl =
                      window.location.origin + window.location.pathname + "?" + searchParams;
                    window.history.replaceState(null, "", newUrl);
                  }
                  onClose();
                },
              }
            );
          },
        },
      ]}
    >
      <Box>
        <FormattedMessage
          id="move.confirm.text"
          defaultMessage="You are about to revert a file to an older version."
        />
      </Box>
      {errors && <RanaErrors errors={errors} />}
    </RanaModal>
  );
}
